import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStorageStore } from '../store/useStorageStore';
import { useDanceStore } from '../store/useDanceStore';
import { TUTORIAL_MODULES } from '../utils/tutorialData';
import { CheckCircle, Play, GraduationCap, Undo2, BookOpen, Video } from 'lucide-react';

export const TutorialModule: React.FC = () => {
    const { t } = useTranslation();
    const tutorialProgress = useStorageStore(state => state.tutorialProgress);
    const updateTutorialProgress = useStorageStore(state => state.updateTutorialProgress);
    const setActiveModule = useDanceStore(state => state.setActiveModule);
    const setDuration = useDanceStore(state => state.setDuration);
    const newSequence = useDanceStore(state => state.newSequence);
    const confirmAction = useDanceStore(state => state.confirmAction);
    const hasUnsavedSequenceChanges = useDanceStore(state => state.hasUnsavedSequenceChanges);
    const keyframes = useDanceStore(state => state.keyframes);
 
    const resetTutorial = useStorageStore(state => state.resetTutorial);
 
    const handleReset = () => {
        confirmAction({
            title: t('tutorial.reset_confirm_title', { defaultValue: 'Reset Training Progress?' }),
            message: t('tutorial.reset_confirm_message', { defaultValue: 'This will clear all your completed modules and current progress. This action cannot be undone.' }),
            confirmLabel: t('tutorial.reset_confirm_btn', { defaultValue: 'Reset Everything' }),
            onConfirm: () => resetTutorial()
        });
    };
 
    const startModule = (moduleId: string) => {
        const start = () => {
            newSequence();
            
            // Set tutorial specific workspace defaults
            if (moduleId === 'introduction') {
                setDuration(3.5);
            }
            
            updateTutorialProgress({
                currentModuleId: moduleId,
                currentStepIndex: 0
            });
            setActiveModule('PoseDesigner'); // Start in the designer to perform actions
        };

        if (keyframes.length > 0 || hasUnsavedSequenceChanges) {
            confirmAction({
                title: t('tutorial.new_tutorial_title', { defaultValue: 'New Tutorial Sequence' }),
                message: t('tutorial.new_tutorial_message', { defaultValue: 'Starting this tutorial will clear your current sequence. Do you want to continue?' }),
                onConfirm: start
            });
        } else {
            start();
        }
    };

    return (
        <div className="explorer-container tutorial-module-container" style={{ zIndex: 40, background: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(20px)', position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <div className="explorer-header tutorial-module-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="flex-row" style={{ gap: '12px' }}>
                    <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GraduationCap size={24} />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.02em', color: '#38bdf8' }}>{t('tutorial.training_center')}</h2>
                        <p className="module-header-subtitle" style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>{t('tutorial.training_center_desc')}</p>
                    </div>
                </div>

                <button 
                    className="timeline-btn red" 
                    onClick={handleReset}
                    style={{ gap: '8px', padding: '0 16px' }}
                >
                    <Undo2 size={16} />
                    <span>{t('tutorial.reset_all_progress', { defaultValue: 'Reset All Progress' })}</span>
                </button>
            </div>

            <div className="explorer-content tutorial-module-content" style={{ overflowY: 'auto', flexGrow: 1, padding: '8px 40px 12px 52px' }}>
                <div className="tutorial-module-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', maxWidth: '1400px', margin: '0 auto' }}>
                    {TUTORIAL_MODULES.map(module => {
                        const isCompleted = tutorialProgress.completedModuleIds.includes(module.id);
                        const isActive = tutorialProgress.currentModuleId === module.id;

                        return (
                            <div 
                                key={module.id} 
                                className="glass-panel" 
                                style={{ 
                                    padding: '6px 10px', 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '4px',
                                    border: isActive ? '1px solid #38bdf8' : '1px solid rgba(255,255,255,0.1)',
                                    background: isActive ? 'rgba(56, 189, 248, 0.08)' : 'rgba(30, 41, 59, 0.5)',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                {isActive && (
                                    <div style={{ position: 'absolute', top: 0, right: 0, background: '#38bdf8', color: '#0f172a', fontSize: '0.6rem', fontWeight: 800, padding: '2px 8px', borderRadius: '0 0 0 6px', textTransform: 'uppercase' }}>
                                        {t('tutorial.current', { defaultValue: 'Current' })}
                                    </div>
                                )}
                                
                                <div className="flex-between">
                                    <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: isCompleted ? '#22c55e' : 'white', lineHeight: 1.1, minHeight: '2.0em', display: 'flex', alignItems: 'center' }}>
                                        {module.title}
                                    </h3>
                                    {isCompleted && <CheckCircle size={13} color="#22c55e" />}
                                </div>
                                
                                <p style={{ fontSize: '0.72rem', opacity: 0.7, margin: 0, minHeight: '2.0em', lineHeight: 1.15, whiteSpace: 'pre-wrap', flexGrow: 1 }}>
                                    {module.description}
                                </p>

                                <div style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', opacity: module.steps.length > 0 ? 1 : 0.3 }}>
                                    <span>{module.steps.length} {module.steps.length === 1 ? t('tutorial.lesson', { defaultValue: 'Lesson' }) : t('tutorial.lessons', { defaultValue: 'Lessons' })}</span>
                                    {module.steps.length === 0 && <span style={{ opacity: 0.5 }}>({t('tutorial.coming_soon', { defaultValue: 'Coming Soon' })})</span>}
                                </div>

                                <button 
                                    className={`timeline-btn ${module.steps.length > 0 ? 'blue' : ''}`}
                                    onClick={() => startModule(module.id)}
                                    disabled={module.steps.length === 0}
                                    style={{ 
                                        width: '100%', 
                                        justifyContent: 'center', 
                                        height: '24px',
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                        marginTop: '0px'
                                    }}
                                >
                                    {module.steps.length === 0 ? t('tutorial.unavailable', { defaultValue: 'Unavailable' }) : (isCompleted ? t('tutorial.redo_module', { defaultValue: 'Redo Module' }) : (isActive ? t('tutorial.resume_module', { defaultValue: 'Resume Module' }) : t('tutorial.start_module', { defaultValue: 'Start Module' })))}
                                </button>
                            </div>
                        );
                    })}

                    {/* Dedicated User Manual Card */}
                    <div 
                        className="glass-panel" 
                        style={{ 
                            padding: '6px 10px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '4px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            background: 'rgba(30, 41, 59, 0.5)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="flex-between">
                            <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#fbbf24', lineHeight: 1.1, minHeight: '2.0em', display: 'flex', alignItems: 'center' }}>
                                {t('tutorial.user_manual_title', { defaultValue: 'User Manual' })}
                            </h3>
                            <div style={{ background: 'rgba(45, 212, 191, 0.15)', color: '#2dd4bf', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <BookOpen size={13} />
                            </div>
                        </div>
                        
                        <p style={{ fontSize: '0.72rem', opacity: 0.7, margin: 0, minHeight: '2.0em', lineHeight: 1.15, whiteSpace: 'pre-wrap', flexGrow: 1 }}>
                            {t('tutorial.user_manual_desc', { defaultValue: 'Detailed guide covering pose designing, sequences, timeline editing, music synchronization, shortcuts, and more.' })}
                        </p>

                        <div style={{ fontSize: '0.72rem', color: '#2dd4bf', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>{t('tutorial.user_manual_lang', { defaultValue: 'Multi-Language Support' })}</span>
                        </div>

                        <button 
                            className="timeline-btn blue"
                            onClick={() => window.open('/usermanual', '_blank')}
                            style={{ 
                                width: '100%', 
                                justifyContent: 'center', 
                                height: '24px',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                marginTop: '0px'
                            }}
                        >
                            {t('tutorial.open_manual', { defaultValue: 'Open Manual' })}
                        </button>
                    </div>

                    {/* Dedicated Video Guides Card */}
                    <div 
                        className="glass-panel" 
                        style={{ 
                            padding: '6px 10px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '4px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            background: 'rgba(30, 41, 59, 0.5)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="flex-between">
                            <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#fbbf24', lineHeight: 1.1, minHeight: '2.0em', display: 'flex', alignItems: 'center' }}>
                                {t('tutorial.video_guides_title', { defaultValue: 'Video Guides' })}
                            </h3>
                            <div style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Video size={13} />
                            </div>
                        </div>
                        
                        <p style={{ fontSize: '0.72rem', opacity: 0.7, margin: 0, minHeight: '2.0em', lineHeight: 1.15, whiteSpace: 'pre-wrap', flexGrow: 1 }}>
                            {t('tutorial.video_guides_desc', { defaultValue: 'Coming Soon' })}
                        </p>

                        <div style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.5 }}>
                            <span>{t('tutorial.video_guides_sub', { defaultValue: 'YouTube Channel' })}</span>
                        </div>

                        <button 
                            className="timeline-btn"
                            disabled={true}
                            style={{ 
                                width: '100%', 
                                justifyContent: 'center', 
                                height: '24px',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                marginTop: '0px'
                            }}
                        >
                            {t('tutorial.watch_guides', { defaultValue: 'Watch Guides' })}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
